import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "@mui/material/Modal";
import StepProgress from "../../components/stepper/StepProgress";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import LoadingOverlay from "../../components/overlays/LoadingOverlay";
import AppNavigation from "../../components/navigation/appNavigation/AppNavigation";
import { handleFileChange } from "../../utils/SubmitContent/fileHandling";
import {
  handleSubmit,
  handleSubmitContent,
} from "../../utils/SubmitContent/handleSubmit";
import { useWordCount } from "../../utils/SubmitContent/wordCount";
import { navigateToQuestionSelection } from "../../utils/SubmitContent/handleQuestionSelectionAPICall.js";
import TitleAndDomainInput from "../../components/submitContentComponents/TitleAndDomainInput";
import HelpingPointer from "../../components/submitContentComponents/HelpingPointers";
import useCanvasDetection from "../../hooks/useCanvasDetection";
import { commonErrorToastMessage } from "../../components/toastify/AllToastifyErrors";
import QuillionzProUpgradeModal from "../../components/modals/QuillionzProUpgradeModal";
import TrySampleText from "../../components/submitContentComponents/TrySampleText.jsx";

import { franc } from "franc";
import { canvasUserLogin } from "../../api/userLogin";
import { youtubeVideo } from "../../api/youtubeVideo";
import SubmitContentUnlimitedModal from "../../components/modals/SubmitContentUnlimitedModal";
import ConfirmFileUploadModal from "../../components/modals/ConfirmFileUploadModal";
import ContentInfoModal from "../SubmitContent/ContentInfoModal.jsx";
import ContentErrorModal from "../SubmitContent/ContentErrorModal.jsx";
import { fetchSampleText } from "../../api/fetchSampleText.js";
import { getQuestionLimit, getRequiredSets } from "../../utils/QuestionLimit.js";

const SubmitContent = ({ hasCanvas }) => {
  const wordsMinLimit = 300; // minimum words limit 300
  const wordsMaxLimit = 15000; // maximum words limit 3000
  const MAX_QUESTIONS_PER_CYCLE = 30; // TODO: @Abhishek Please create Constants file and move these values over there

  const location = useLocation();
  // State variables to manage the selected file and content input
  const [selectedFile, setSelectedFile] = useState(null);
  const [content, setContent] = useState("");

  // New state variable to store the content from the uploaded file
  const [uploadedContent, setUploadedContent] = useState("");

  //  Select domain dropdown
  const [domain, setDomain] = useState("Others");

  const [isNetworkError, setIsNetworkError] = useState(false);

  // Add this state variable at the beginning of the component
  const [title, setTitle] = useState("");

  // Add this state variable at the beginning of the component
  const [youtubeModalResponseData, setYoutubeModalResponseData] = useState("");

  // Add this state variable at the beginning of the component
  const [isYoutubeModalOpen, setIsYoutubeModalOpen] = useState(false);

  // Open Manage Subscription Modal
  const [showManageSubscription, setShowManageSubscription] = useState(false);

  // State to open GoForUnlimited Modal
  const [showGoForUnlimitedModal, setShowGoForUnlimitedModal] = useState(false);

  // Create a state variable to manage the input value
  const [youtubeLink, setYoutubeLink] = useState("");

  // Create a state variable to manage the error message
  const [errorMessage, setErrorMessage] = useState("");

  const titleInputRef = useRef(null);

  const domainSelectRef = useRef(null);

  const [isFileUploaded, setIsFileUploaded] = useState(false);

  const navigate = useNavigate();

  const [isSampleButtonClicked, setIsSampleButtonClicked] = useState(false);
  const [sampleButtonText, setSampleButtonText] = useState("Try a Sample Text");
  // New state variable to store the doc_id received from the API response
  const [doc_id, setDoc_id] = useState("");
  const [responseData, setResponseData] = useState(null);

  // Add a new state variable to track the loading state
  const [isLoading, setIsLoading] = useState(false);

  // Only for text enter
  const [isTextLoading, setIsTextLoading] = useState(false);

  // Switch button for Express Mode
  const [checked, setChecked] = useState(false);

  const [loadingContent, setLoadingContent] = useState(false);

  // Update Word Count
  const { wordCount, updateWordCount } = useWordCount(content);

  const [isGptActive, setIsGptActive] = useState(
    localStorage.getItem("isGptActive") || ""
  );

  const [isDropupOpen, setIsDropupOpen] = useState(false);

  const toggleDropup = () => {
    setIsDropupOpen((prev) => !prev);
  };

  const [showTooltip, setShowTooltip] = useState(true); // Add this line
  // State to store the user attempts i.e Remaining Question Sets
  const [userAttempts, setUserAttempts] = useState(
    Number(localStorage.getItem("UserAttempts")) || 0
  );
  // Helper to always read numeric attempts from localStorage (handles string values in UAT/prod)
  const getNumericUserAttempts = () => {
    const v = localStorage.getItem("UserAttempts");
    return Number(v === null ? userAttempts : v) || 0;
  };

  // Normalize Version once (always lower-case) to avoid casing issues in prod
  const getVersionNormalized = () => (localStorage.getItem("Version") || "").toLowerCase();

  // update state if localStorage is changed in another tab/process (UAT might update via other flows)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "UserAttempts") {
        setUserAttempts(Number(e.newValue) || 0);
      }
      if (e.key === "Version") {
        // optional: force re-render if version changes
        // no-op here, but kept for future use
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const [isContentInfoModalOpen, setIsContentInfoModalOpen] = useState(false);
  const [isContentErrorModalOpen, setIsContentErrorModalOpen] = useState(false);

  // Function to open Manage Subscription Modal
  const handleManageSubscriptionModal = () => {
    // setShowManageSubscription(true);
    navigate("/userinfo");
  };

  // Function to open GoForUnlimitedModal
  const handleGoForUnlimitedModal = () => {
    setShowGoForUnlimitedModal(true);
  };

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleDropdownChange = (event) => {
    setDomain(event.target.value);
  };

  // Function to open the "Youtube Modal"
  const handleYoutubeModalOpen = () => {
    setIsYoutubeModalOpen(true);
    setShowTooltip(false);
  };

  // Set the active step value to 1
  const activeStep = 1;

  const countWords = (text) => {
    const words = text.trim().split(/\s+/);

    return words.length;
  };

  // Calculate the word count conditionally and give color to word count text
  const isWordCountLessThan300 =
    (wordCount < wordsMinLimit || wordCount > wordsMaxLimit) && wordCount >= 1;
  const wordCountClass = isWordCountLessThan300 ? "text-red-500" : "";

  // Function to handle changes in the content input textarea
  const handleTextAreaChange = (event) => {
    if (isSampleButtonClicked) {
      return;
    }
    const newContent = event.target.value.replace(/\s+/g, ' ');

    // TODO:We have kept only the first wordsMaxLimit  words of the original content.
    const words = newContent.trim().split(/\s+/);

    // Trim the content if it exceeds wordsMaxLimit words
    const trimmedContent = words.slice(0, wordsMaxLimit).join(" ");
    const wordCountValue = Math.min(words.length, wordsMaxLimit);

    if (words.length > wordsMaxLimit) {
      setContent(trimmedContent);
      updateWordCount(trimmedContent);

      // Update QAreqbody in localStorage
      const QAreqbody = JSON.parse(localStorage.getItem("QAreqbody") || "{}");
      QAreqbody.text = trimmedContent;
      QAreqbody.total_words = wordCountValue;
      localStorage.setItem("QAreqbody", JSON.stringify(QAreqbody));
      return;
    } else {
      setContent(newContent);
      updateWordCount(newContent);

      // Update QAreqbody in localStorage
      const QAreqbody = JSON.parse(localStorage.getItem("QAreqbody") || "{}");
      QAreqbody.text = newContent;
      QAreqbody.total_words = wordCountValue;
      localStorage.setItem("QAreqbody", JSON.stringify(QAreqbody));
    }
  };

  const handleTextAreaChangeForFileUpload = (event) => {
    const newContent = "";

    updateWordCount(newContent);
  };

  const textareaRef = useRef();

  // For Canvas
  useEffect(() => {
    // Set focus on the textarea when the component mounts
    (async () => {
      if (hasCanvas) {
        const { data, status } = await canvasUserLogin();
        if ([200, 201].includes(status)) {
          const {
            token,
            username,
            email,
            remainingQuestionSets,
            currentDate,
            Usergpt_key,
            userId,
            isGptActive,
            Plan_Validity_Subscription,
            ExpiryDate,
            remainingDaysInMonth,
            calculatedDate,
            isPlanExpired,
          } = data;
          localStorage.setItem("token", token);
          localStorage.setItem("email", email);
          localStorage.setItem("username", username);
          localStorage.setItem("Id", userId);
          const numericRemaining = Number(remainingQuestionSets) || 0;
          localStorage.setItem("UserLoggedIn", currentDate);
          localStorage.setItem("userGPTKey", Usergpt_key);
          localStorage.setItem("isGptActive", isGptActive);
          setIsGptActive(isGptActive);
          setUserAttempts(numericRemaining);
          localStorage.setItem("remainingDaysInMonth", remainingDaysInMonth);
          localStorage.setItem("calculatedDate", calculatedDate);
          localStorage.setItem("ExpiryDate", ExpiryDate);
          localStorage.setItem(
            "Plan_Validity_Subscription",
            Plan_Validity_Subscription
          );
          localStorage.setItem("isPlanExpired", isPlanExpired);
        }
      }
    })();
    textareaRef?.current?.focus();
  }, []);

  // Function to handle the "Try Sample" button click
  const handleSampleButtonClick = async (selectedOption) => {
    setIsSampleButtonClicked(true);

    let title = "";
    let category = "";
    let subCategory = "";
    let content = "";
    let buttonText = selectedOption; // Update button text dynamically

    switch (selectedOption) {
      case "Private Pension":
        title = "Private Pension Policy";
        category = "Retirement Planning & Pensions";
        subCategory = "Pension Plans";
        break;

      case "Tracker Rate Mortgage":
        title = "Tracker Rate Mortgage Agreement";
        category = "Small Business Financial Management";
        subCategory = "Business Funding";
        break;

      case "Fixed Rate Mortgage":
        title = "Fixed Rate Mortgage Agreement";
        category = "Personal Budgeting & Debt Management";
        subCategory = "Secured Lending";
        break;

      case "Home Insurance":
        title = "Home Contents Insurance Policy";
        category = "Insurance & Risk Management";
        subCategory = "Property Insurance";
        break;

      case "Car Insurance":
        title = "Comprehensive Car Insurance Policy";
        category = "Insurance & Risk Management";
        subCategory = "Liability Insurance";
        break;

      default:
        title = "Exploring Scientific Frontiers";
        category = "Others";
        subCategory = "Others";
        buttonText = "Try a Sample Text"; // Reset if invalid option
    }

    const sample_data = await getSampleText(category, subCategory);
    setTitle(title);
    let sampletext = cleanApiText(sample_data?.context);
    setContent(sampletext);
    setSampleButtonText(buttonText);
    localStorage.setItem("isSampleButtonClicked", true);
    localStorage.setItem("title", title);
    localStorage.setItem("category", category);
    localStorage.setItem("subCategory", subCategory);
    localStorage.setItem("content", sample_data?.context);
    localStorage.setItem("doc_id", sample_data?.doc_id);
  };

  async function getSampleText(category, subCategory) {
    const userEmail = localStorage.getItem("email");
    const { data, status } = await fetchSampleText(
      userEmail,
      category,
      subCategory
    );
    if ([200, 201].includes(status)) {
      return data;
    }
  }

  // Reference to the file input element
  const fileInputRef = useRef(null);

  let pdfResponseData;

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Handle File Input Change
  const handleFileInputChange = async (event) => {
    // Store the file temporarily in the state or a ref
    const file = event.target.files[0];
    if (file) {
      // Check if the file size is less than 10MB
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes

      if (file.size > maxSize) {
        commonErrorToastMessage("File size must be less than 10MB.");
        // Reset the file input value to allow selecting a different file
        fileInputRef.current.value = "";
        setShowTooltip(false);

        // Trigger a click event to open the file dialog
        fileInputRef.current.click();
        return;
      }

      // Open the confirmation modal
      setIsConfirmModalOpen(true);
      // Store the file in state for later use
      setSelectedFile(file);
    }

    // Further process only if the user confirmed
    if (isConfirmModalOpen) {
      // Reset state and perform additional actions
      setIsConfirmModalOpen(false); // Close the confirmation modal
      setContent("");
      setLoadingContent(true);

      // Update content only if word count is less than or equal to wordsMaxLimit
      setContent((prevContent) => {
        const words = event.target.value.trim().split(/\s+/);
        const newContent = event.target.value;

        if (words.length <= wordsMaxLimit) {
          return newContent;
        }

        return prevContent; // Maintain the previous content if the condition is not met
      });

      // Perform other actions as needed
    }
  };

  const handleConfirmUpload = async () => {
    setIsConfirmModalOpen(false);
    // Now call handleFileChange with the selected file
    if (selectedFile) {
      try {
        const newContent = await handleFileChange(
          selectedFile,
          title,
          content,
          wordCount,
          updateWordCount,
          setResponseData,
          setSelectedFile,
          setIsFileUploaded,
          setLoadingContent,
          setIsLoading,
          setTitle,
          setContent,
          setDomain,
          setDoc_id,
          domain
        );

        if (
          newContent === undefined ||
          newContent === null ||
          newContent === ""
        ) {
          setContent("");
          setSelectedFile(null);
        }

        // Update the textarea content only if the file is valid
        if (newContent !== null) {
          localStorage.setItem(
            "QAreqbody",
            JSON.stringify(newContent.fullResponse.data)
          );
          let newText = cleanApiText(newContent?.text);
          setContent(newText);
        }
      } catch (error) {
        setContent("");
        console.log("Error while uploading PDF Submit Content Page :  ", error);
      }
    }
  };

  const handleCancelUpload = () => {
    setIsConfirmModalOpen(false);
    setSelectedFile(null);
    setTitle("");
    setContent("");
    setLoadingContent(false);
  };

  // Function to trigger the click event on the file input element
  const handleUploadButtonClick = () => {
    // Title is coming undefined whenever the user uploads any file without entering the title. For that, we add this logic so that he would get an error message before uploading the file
    if (!title) {
      commonErrorToastMessage(
        "Please provide the title before you upload the file."
      );
      return;
    }

    // Access the file input element
    const fileInput = fileInputRef.current;

    // Check if file input is available
    if (!fileInput) {
      commonErrorToastMessage("File input is not available.");
      return;
    }

    // Check if any file is selected
    if (fileInput.files.length === 0) {
      // Trigger a click event to open the file dialog
      fileInput.click();
    } else {
      // Check if the file size is less than 10MB
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      const fileSize = fileInput.files[0].size;

      if (fileSize > maxSize) {
        commonErrorToastMessage("File size must be less than 10MB.");
      } else {
        // If all checks pass, you can proceed with your upload logic here
        // For now, I'm triggering a click event to simulate further steps
        fileInput.click();
      }
    }
  };

  // Function to handle the "Proceed" button
  const handleProceedClick = () => {
    if (Version && Version.toLowerCase() === "free" && !isSampleButtonClicked) {
      // Prevent Free users from submitting — show pricing/upgrade popup
      //  commonErrorToastMessage("You must subscribe to a paid plan to submit content.");
      setShowManageSubscription(true);
      return;
    }

    if (!title.trim() && !domain.trim() && !content.trim()) {
      commonErrorToastMessage(
        "Please add title, domain and content to proceed."
      );
      return;
    }

    if (!title.trim()) {
      commonErrorToastMessage("Please add title for your content.");
      return;
    }

    if (!domain.trim()) {
      setDomain("Other");
    }

    if (!content.trim()) {
      commonErrorToastMessage("Please provide the content to proceed.");
      return;
    }
  };

  // Handler for Ok button in modal
  const handleContentInfoModalOk = async () => {
    try {
      setIsContentInfoModalOpen(false);

      const attempts = Number(userAttempts) || 0;
      const isSample =
        isSampleButtonClicked ||
        localStorage.getItem("isSampleButtonClicked") === "true";

      // If not using sample flow, block GPT users with no attempts left
      if (!isSample && Version !== "Free" && attempts <= 0) {
        commonErrorToastMessage("Your Count have been expired.");
        return;
      }
      if (!title.trim()) {
        commonErrorToastMessage("Please add title for your content.");
        return;
      }
      setIsLoading(true); // Show loader
      const QAreqbodyRaw = localStorage.getItem("QAreqbody");
      let hasDocId = false;
      if (QAreqbodyRaw) {
        try {
          const QAobj =
            typeof QAreqbodyRaw === "string"
              ? JSON.parse(QAreqbodyRaw)
              : QAreqbodyRaw;
          // Check common locations for doc_id (top-level or inside fullResponse.data)
          hasDocId = !!(
            (QAobj && QAobj.doc_id) ||
            (QAobj &&
              QAobj.fullResponse &&
              QAobj.fullResponse.data &&
              QAobj.fullResponse.data.doc_id)
          );
        } catch (parseErr) {
          console.warn(
            "Failed to parse QAreqbody from localStorage:",
            parseErr
          );
          hasDocId = false;
        }
      }
      if (!hasDocId) {
        // No doc_id -> perform full submit flow
        await handleSubmitContentButton();
      } else {
        // doc_id exists -> skip submit and navigate to question selection
        await new Promise((resolve) => setTimeout(resolve, 5000));
        navigate(`/QuestionsSelection`);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false); // Hide loader
      // Handle error (show toast, etc.)
      console.error("Error uploading text:", error);
      // Optionally show an error modal or toast here
    }
  };

  // Handler for Cancel button in modal
  const handleContentInfoModalCancel = () => {
    setIsContentInfoModalOpen(false);
  };

  const handleContentErrorModalCancel = () => {
    setIsContentErrorModalOpen(false);
  };

  let myAreaHeight = "60vh";

  // Calculate the height of the textarea based on different conditions
  if (selectedFile) {
    // Height of the textarea when a file is uploaded
    myAreaHeight = "33vh";
  } else if (content) {
    // Height of the textarea when content is inserted
    myAreaHeight = "39vh";
  }

  // Function to handle form submission and API call
  const handleSubmitContentButton = async () => {
    await handleSubmitContent({
      title,
      domain,
      wordCount,
      selectedFile,
      uploadedContent,
      content,
      setContent,
      setIsLoading,
      setIsTextLoading,
      setTitle,
      setDomain,
      setIsNetworkError,
      setResponseData,
      titleInputRef,
      domainSelectRef,
      navigate,
    });
  };

  // Function to handle navigation to "Question Selection Screen (QSS)" page
  const handleQSSNavigation = async () => {
    await navigateToQuestionSelection(
      title,
      domain,
      wordCount,
      doc_id,
      content,
      responseData,
      titleInputRef,
      domainSelectRef,
      setIsLoading,
      setIsTextLoading,
      navigate,
      selectedFile
    );
  };

  const isCanvas = useCanvasDetection();

  const remainingQuestionSets = localStorage.getItem("UserAttempts");
  const remainingDaysInMonth = localStorage.getItem("monthEndDate");
  const planExpiryDate = localStorage.getItem("planExpiryDate");
  // Formatting the date
  const getFormattedMonth = (dateString) => {
    const date = new Date(dateString);
    const options = { month: "short", year: "numeric", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  // Assuming the value of monthEndDate is in 'MM/DD/YYYY' format
  const formattedMonthEndDate = getFormattedMonth(remainingDaysInMonth);

  if (userAttempts === "undefined") {
    localStorage.setItem("UserAttempts", 0);
  }

  const isPlanExpired = localStorage.getItem("isPlanExpired");
  const Version = localStorage.getItem("Version");
  const TIERTYPE = localStorage.getItem("TIERTYPE");
  const user_tier =
    TIERTYPE === "tier2_monthly" || TIERTYPE === "tier2_annually" ? 2 : 1;

  // Plan expired message logic :
  const [subscriptionPlan, setSubscriptionPlan] = useState("");

  // Effect hook to update subscription plan name based on the value
  useEffect(() => {
    const planNumber = parseInt(localStorage.getItem("subscriptionPlan"));
    let planName = "";

    switch (planNumber) {
      case 1:
        planName = "Monthly";
        break;
      case 3:
        planName = "Quarterly";
        break;
      case 12:
        planName = "Annual";
        break;
      default:
        planName = "Free";
        break;
    }

    setSubscriptionPlan(planName);
  }, []); // Empty dependency array ensures this effect runs only once when the component mounts.

  function cleanApiText(str) {
    return str
      .replace(/\\/g, "") // remove escape slashes
      .replace(/\*\*(.*?)\*\*/g, "$1") // remove bold markdown
      .replace(/\*(.*?)\*/g, "$1"); // remove italics
  }

  // Helper: compute required sets and open appropriate modal
  const getRequiredSetsForWords = (wc) => {
    // Use central helper which reads QuestionLimitNo / Version from localStorage
    return getRequiredSets(Number(wc) || 0);
  };

  const openContentModalForWordCount = (wc) => {
    const requiredSets = getRequiredSetsForWords(wc);
    // Sample or Free always allowed (use normalized version)
    if (isSampleButtonClicked || getVersionNormalized() === "free") {
      setIsContentInfoModalOpen(true);
      return;
    }
    // Ensure numeric attempts (fresh read)
    const attempts = getNumericUserAttempts();
    // debug - temporarily log values on UAT (remove after verification)
    // console.debug('openContentModalForWordCount', { wc, requiredSets, attempts });
    // show error only when required sets strictly exceed remaining sets
    if (requiredSets > attempts) {
    console.log("requiredSets > attempts", { requiredSets, attempts });
      setIsContentErrorModalOpen(true);
    } else {
    console.log("requiredSets <= attempts", { requiredSets, attempts });
      setIsContentInfoModalOpen(true);
    }
  };

  return (
    <div className=" h-[100vh] overflow-hidden">
      {/* {isCanvas ? null : <AppNavigation />} */}
      {/* App Navigation */}
      <AppNavigation
        setTitle={setTitle}
        setContent={setContent}
        setDomain={setDomain}
        setSelectedFile={setSelectedFile}
      />
      {/* Stepper component (present of rightmost side) */}
      <div className="w-auto h-auto step-progress">
        {/* Pass the activeStep value to the StepProgress component */}
        <StepProgress activeStep={activeStep} />
      </div>
      <div className={`bg-white h-[81vh] select-none `}>
        <div className=" flex ">
          {/* Conditionally displaying the "TitleAndDomainInput" and "HelperPointer" components */}
          {!isPlanExpired || isPlanExpired === "false" ? (
            remainingQuestionSets != 0 ||
            isGptActive == 1 ||
            localStorage.getItem("userGPTKey") == "LIMITED" ? (
              <div className=" w-full  h-auto px-6 pt-0 rounded-2xl mx-5">
                <TitleAndDomainInput
                  title={title}
                  setTitle={setTitle}
                  titleInputRef={titleInputRef}
                  checked={checked}
                  handleChange={handleChange}
                  domain={domain}
                  setDomain={setDomain}
                  handleDropdownChange={handleDropdownChange}
                  domainSelectRef={domainSelectRef}
                />

                <div className="mt-[10px]">
                  {/* Text area for content input starts here */}
                  <div
                    className={`p-5 mx-auto custom-scrollbar my-area opacity-80 ${
                      isCanvas ? "h-[63vh]" : "h-[56vh] "
                    }`}
                    style={{
                      minHeight: "33vh",
                      maxHeight: "81vh",
                      width: "90%",
                      marginTop: "3px",
                      marginBottom: "3px",
                      background: "#f9fbff",
                      // If the valid file is uploaded or any text is entered then size of text area changes accordingly (myAreaHeight function)
                      // height: "70vh",
                      overflowY: content ? "hidden" : "hidden", // Add this line to enable vertical scrolling
                      border: "2px solid #ccbfff", // 👈 add this line
                      borderRadius: "8px", // optional: rounded corners
                    }}
                  >
                    {loadingContent ? (
                      <div className="mt-[30%]">
                        <HelpingPointer />
                      </div>
                    ) : (
                      <textarea
                        className={`w-full h-full resize-none bg-[#f9fbff]  p-2 text-[#212529] font-normal text-lg leading-6 outline-none custom-scrollbar  ${
                          isSampleButtonClicked ? "" : ""
                        }`}
                        value={content}
                        ref={textareaRef}
                        onChange={handleTextAreaChange}
                      ></textarea>
                    )}

                    {/* Pointers */}
                    {!selectedFile && !content && (
                      <div className="">
                        <HelpingPointer
                          isSampleButtonClicked={isSampleButtonClicked}
                          content={content}
                          handleTextAreaChange={
                            handleTextAreaChangeForFileUpload
                          }
                          handleUploadButtonClick={handleUploadButtonClick}
                          handleFileInputChange={handleFileInputChange}
                          handleYoutubeModalOpen={handleYoutubeModalOpen}
                          isFileUploaded={isFileUploaded}
                          textareaRef={textareaRef}
                          fileInputRef={fileInputRef}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="mt-[13px]">
                      <div className="flex  h-[13vh] pr-[35px] align-middle justify-center place-content-center border-t-[1px] ">
                        <div className="w-[45%] ">
                          <p
                            className={`font-semibold mt-2 text-xs ${wordCountClass} text-[#777e8d] `}
                          >
                            *Total words considered : {wordCount} (Word count
                            must be between {wordsMinLimit}-{wordsMaxLimit} for
                            quiz generation.)
                          </p>

                          <p className="text-[11px] text-[#777e8d] pl-[5px]">
                            English Content only.
                          </p>
                        </div>

                        <div className="mt-3  w-[50%] flex justify-end align-middle gap-10">
                          {Version === "Free" ? (
                            <div className="pt-2.5">
                              {" "}
                              <TrySampleText
                                handleSampleButtonClick={
                                  handleSampleButtonClick
                                }
                                isDropupOpen={isDropupOpen}
                                toggleDropup={toggleDropup}
                                sampleButtonText={sampleButtonText}
                              />
                            </div>
                          ) : null}

                          <div className="">
                            {selectedFile || content ? (
                              ""
                            ) : (
                              <div className="flex gap-11">
                                <Tooltip
                                  title="Submit"
                                  arrow
                                  placement="top"
                                  TransitionComponent={Zoom}
                                  TransitionProps={{ timeout: 300 }}
                                >
                                  <button
                                    className="rounded py-2 mt-2 h-[35px] hover:opacity-90 bg-[#c6530c] text-white text-sm  px-4  "
                                    onClick={handleProceedClick}
                                  >
                                    Submit
                                  </button>
                                </Tooltip>
                              </div>
                            )}

                            {selectedFile ? (
                              <Tooltip
                                title="Submit your PDF content"
                                arrow
                                placement="top"
                                TransitionComponent={Zoom}
                                TransitionProps={{ timeout: 300 }}
                              >
                                <button
                                  className={`rounded mt-2 h-[35px] hover:opacity-90 ${
                                    isTextLoading
                                      ? "bg-[#0c3f5a] "
                                      : "bg-[#c6530c]"
                                  } text-white px-4 flex place-content-center align-middle justify-center flex-col`}
                                  onClick={() => {
                                    if (
                                      userAttempts <= 0 &&
                                      isSampleButtonClicked &&
                                      Version.toLowerCase() !== "free"
                                    ) {
                                      if (userAttempts <= 0) {
                                        commonErrorToastMessage(
                                          "Want to create more? Upgrade your plan!"
                                        );
                                        return;
                                      }
                                      setShowManageSubscription(true);
                                      return;
                                    }
                                    if (
                                      Version === "Free" ||
                                      Version === "free"
                                    ) {
                                      if (
                                        Version &&
                                        Version.toLowerCase() === "free" &&
                                        !isSampleButtonClicked
                                      ) {
                                        setShowManageSubscription(true);
                                        return;
                                      }
                                    }
                                    if (wordCount < wordsMinLimit) {
                                      commonErrorToastMessage(
                                        "Word count must be at least 300 for quiz generation."
                                      );
                                      return;
                                    }
                                    openContentModalForWordCount(wordCount);
                                  }}
                                >
                                  {isTextLoading ? (
                                    <>
                                      <span className="loader"></span>
                                      <span className=" ms-4">
                                        Processing Content
                                      </span>
                                    </>
                                  ) : (
                                    "Submit"
                                  )}
                                </button>
                              </Tooltip>
                            ) : content ? (
                              <Tooltip
                                title="Submit your content"
                                arrow
                                placement="top"
                                TransitionComponent={Zoom}
                                TransitionProps={{ timeout: 300 }}
                              >
                                <button
                                  className={`rounded mt-2 h-[7vh] hover:opacity-90 ${
                                    isTextLoading
                                      ? "bg-[#0c3f5a] "
                                      : "bg-[#c6530c]"
                                  } text-white px-4 flex place-content-center align-middle justify-center flex-col`}
                                  onClick={() => {
                                    if (
                                      getNumericUserAttempts() <= 0 &&
                                      !isSampleButtonClicked &&
                                      getVersionNormalized() !== "free"
                                    ) {
                                      commonErrorToastMessage(
                                        "Want to create more? Upgrade your plan!"
                                      );
                                      setShowManageSubscription(true);
                                      return;
                                    }
                                    if (
                                      Version === "Free" ||
                                      Version === "free"
                                    ) {
                                      if (
                                        Version &&
                                        Version.toLowerCase() === "free" &&
                                        !isSampleButtonClicked
                                      ) {
                                        setShowManageSubscription(true);
                                        return;
                                      }
                                    }
                                    if (wordCount < wordsMinLimit) {
                                      commonErrorToastMessage(
                                        "Word count must be at least 300 for quiz generation."
                                      );
                                      return;
                                    }
                                    openContentModalForWordCount(wordCount);
                                  }}
                                >
                                  {isTextLoading ? (
                                    <>
                                      <span className="loader "></span>
                                      <span className="ms-5 ">
                                        Processing Content
                                      </span>
                                    </>
                                  ) : (
                                    "Submit"
                                  )}
                                </button>
                              </Tooltip>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Text area for content input ends here */}
              </div>
            ) : (
              // This screen will appear when a user reaches their monthly limit of 25 question sets.
              <div className=" w-full h-[81vh] m-auto flex flex-col justify-center align-middle items-center place-content-center  px-6 pt-4 rounded-2xl mx-5">
                <div className="border rounded-xl p-5 shadow-xl hover:shadow-myShadow2 flex flex-col justify-center align-middle items-center place-content-center">
                  <i className="fa-solid fa-triangle-exclamation text-red-400 fa-5x mb-4"></i>

                  <p>
                    You have{" "}
                    <span className="font-semibold text-red-500">
                      0 out of 5
                    </span>{" "}
                    Quiz sets available for this month till{" "}
                    <span className="font-semibold">{planExpiryDate}.</span>
                  </p>

                  <p className="mt-5">
                    For more Quiz sets, upgrade to the{" "}
                    <span className="font-semibold"> plan</span>.
                  </p>

                  <p className="mt-5">
                    To change subscription duration{" "}
                    <Tooltip
                      title="Manage Subscription"
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <button
                        className=" text-[#f4833d]  underline hover:no-underline"
                        onClick={handleManageSubscriptionModal}
                      >
                        Click here
                      </button>
                    </Tooltip>
                    .
                  </p>

                  <div className="flex">
                    {Version === "Free" ? (
                      <>
                        <p>
                          <Tooltip
                            title="Purchase plan"
                            arrow
                            placement="top"
                            TransitionComponent={Zoom}
                            TransitionProps={{ timeout: 300 }}
                          >
                            <button
                              className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] "
                              onClick={handleGoForUnlimitedModal}
                            >
                              Purchase plan
                            </button>
                          </Tooltip>
                        </p>
                      </>
                    ) : (
                      <>
                        <p>
                          <Tooltip
                            title="Upgrade to plan"
                            arrow
                            placement="top"
                            TransitionComponent={Zoom}
                            TransitionProps={{ timeout: 300 }}
                          >
                            <button
                              className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] "
                              onClick={handleGoForUnlimitedModal}
                            >
                              Upgrade to plan
                            </button>
                          </Tooltip>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          ) : (
            // This screen will appear when a user reaches their monthly limit of 25 question sets.
            <div className=" w-full h-[81vh] m-auto flex flex-col justify-center align-middle items-center place-content-center  px-6 pt-4 rounded-2xl mx-5">
              <div className="border rounded-xl p-5 shadow-xl hover:shadow-myShadow2 flex flex-col justify-center align-middle items-center place-content-center">
                <i className="fa-solid fa-triangle-exclamation text-red-400 fa-5x mb-4"></i>
                <p className="mt-5">Want to create more? Upgrade your plan!</p>
                {isPlanExpired ? (
                  ""
                ) : (
                  <p className="mt-5">
                    For more quiz sets, upgrade to the{" "}
                    <span className="font-semibold">plan</span>.
                  </p>
                )}

                {isPlanExpired ? (
                  <p className="mt-5">
                    To change your subscription duration, please click the
                    button below.
                  </p>
                ) : (
                  <p className="mt-5">
                    To change your subscription duration, please click the
                    button below
                    <Tooltip
                      title="Manage Subscription"
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <button
                        className=" text-[#0d3e58]  underline hover:no-underline"
                        onClick={handleManageSubscriptionModal}
                      >
                        Click here
                      </button>
                    </Tooltip>
                    .
                  </p>
                )}

                <div className="flex    ">
                  {isPlanExpired || isPlanExpired === "undefined" ? (
                    <p className="">
                      {" "}
                      <Tooltip
                        title="Manage Subscription"
                        arrow
                        placement="top"
                        TransitionComponent={Zoom}
                        TransitionProps={{ timeout: 300 }}
                      >
                        <button
                          className="rounded mt-4 px-6 py-2 bg-[#0d3e58] text-white  hover:bg-[#0a6697] "
                          onClick={handleManageSubscriptionModal}
                        >
                          Subscribe Now
                        </button>
                      </Tooltip>
                    </p>
                  ) : (
                    <>
                      <p>
                        <Tooltip
                          title="Upgrade to plan"
                          arrow
                          placement="top"
                          TransitionComponent={Zoom}
                          TransitionProps={{ timeout: 300 }}
                        >
                          <button
                            className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] "
                            onClick={handleGoForUnlimitedModal}
                          >
                            Upgrade to plan
                          </button>
                        </Tooltip>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Loading overlay */}
        {isLoading && <LoadingOverlay />}

        {/* Render the Go Premium Modal component */}
        <QuillionzProUpgradeModal
          isOpen={showManageSubscription}
          onClose={() => setShowManageSubscription(false)}
        />

        {/* If a user reaches their monthly limit of 25 question sets, they can open the modal for "Unlimited Question Sets"." */}
        <SubmitContentUnlimitedModal
          open={showGoForUnlimitedModal}
          onClose={() => setShowGoForUnlimitedModal(false)}
          hideFirstPartForSubmitPage={showGoForUnlimitedModal}
          setShowGoForUnlimitedModal={setShowGoForUnlimitedModal}
        />

        <ConfirmFileUploadModal
          isOpen={isConfirmModalOpen}
          onConfirm={handleConfirmUpload}
          onCancel={handleCancelUpload}
          fileName={selectedFile ? selectedFile.name : ""}
        />

        <ContentInfoModal
          open={isContentInfoModalOpen}
          onClose={handleContentInfoModalCancel}
          handleContentInfoModalOk={handleContentInfoModalOk}
          wordCount={wordCount}
        />
        <ContentErrorModal
          open={isContentErrorModalOpen}
          onClose={handleContentErrorModalCancel}
        />
      </div>
    </div>
  );
};

export default SubmitContent;
